import { useQueryClient } from '@tanstack/react-query'
import { onValue } from 'firebase/database'
import { useEffect } from 'react'

import { useIndividualsServiceGetV1IndividualsByIndividualIdKey } from '@/api/client/queries/common'
import { getRealtimeRef } from '@/integrations/firebase'

/**
 * Subscribes to RTDB for each draft document's node and invalidates the
 * individual query whenever the node receives an update.
 *
 * Why this hook exists: the storage trigger writes pre-check results to RTDB
 * at `individuals/{indId}/documents/{docId}` while the user is still on the
 * upload page or wizard. Without a live subscription the frontend has to wait
 * for the user to navigate or reload before the new `preChecks` reaches the
 * UI. Invalidating the individual query on each RTDB push triggers a refetch
 * that brings the fresh document data (including preChecks) into the cache.
 *
 * The existing `useDocumentUploadStatus` hook does something similar for
 * `status` but stores the snapshot in its own cache key — pages that read
 * `doc.preChecks` from the individual query wouldn't see those updates. This
 * hook closes that gap by invalidating the individual query directly.
 */
export const useDraftDocumentsLiveSync = (
  individualId: string | undefined,
  draftIds: string[],
) => {
  const queryClient = useQueryClient()
  // Stable string key so the effect re-subscribes only when the *contents*
  // change, not on every render that produces a new array reference.
  const draftIdsKey = draftIds.join(',')

  useEffect(() => {
    if (!individualId || draftIdsKey === '') return
    const ids = draftIdsKey.split(',')

    const unsubscribes = ids.map((documentId) => {
      const dbRef = getRealtimeRef(`individuals/${individualId}/documents/${documentId}`)
      return onValue(dbRef, (snapshot) => {
        if (!snapshot.exists()) return
        queryClient.invalidateQueries({
          queryKey: [useIndividualsServiceGetV1IndividualsByIndividualIdKey],
        })
      })
    })

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe())
    }
  }, [individualId, draftIdsKey, queryClient])
}
